import React, { useState } from "react";
import { BaseDropdown } from "@components/library/UI";

import { ReactComponent as ArrowIcon } from "@assets/images/icons/arrow-down-icon.svg";
import { ReactComponent as ArrowRightIcon } from "@assets/images/icons/arrow-right-white-icon.svg";
import { ReactComponent as CalendarIcon } from "@assets/images/icons/calendar.svg";

import "./DateRangePicker.sass";

// --- NATIVE DATE HELPERS ---
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getDaysInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
      date: new Date(year, month - 1, daysInPrevMonth - i),
    });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({
      day: i,
      isCurrentMonth: false,
      date: new Date(year, month + 1, i),
    });
  }
  return days;
};

const formatDate = (date) => {
  if (!date) return "__/__/____";
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate(),
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

const generateMonthOptions = () => {
  const options = [];
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  for (let y = 2024; y <= currentYear; y++) {
    const maxMonth = y === currentYear ? currentMonth : 11;

    for (let m = 0; m <= maxMonth; m++) {
      options.push({ month: m, year: y, label: `${months[m]} ${y}` });
    }
  }

  return options.reverse();
};
const monthOptions = generateMonthOptions();

export const DateRangePicker = ({ onConfirm }) => {
  const today = new Date();

  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth() - 1, 1),
  );
  const [openDropdown, setOpenDropdown] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const year1 = viewDate.getFullYear();
  const month1 = viewDate.getMonth();
  const month1Days = getDaysInMonth(year1, month1);

  const date2 = new Date(year1, month1 + 1, 1);
  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();
  const month2Days = getDaysInMonth(year2, month2);

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (date < startDate) {
      setStartDate(date);
      setEndDate(null);
    } else {
      setEndDate(date);
    }
  };

  const isSelected = (date) => {
    if (!startDate) return false;
    return (
      date.getTime() === startDate.getTime() ||
      (endDate && date.getTime() === endDate.getTime())
    );
  };

  const isInRange = (date) => {
    if (startDate && endDate) {
      return date > startDate && date < endDate;
    }
    return false;
  };

  const renderCalendar = (monthIndex, year, days, position) => {
    const isDropdownOpen = openDropdown === position;

    return (
      <div className="date-picker__calendar">
        <div className="date-picker__month-selector">
          <div
            className="date-picker__month-trigger"
            onClick={() => setOpenDropdown(isDropdownOpen ? null : position)}
          >
            {months[monthIndex]} {year} <ArrowIcon />
          </div>

          {isDropdownOpen && (
            <div className="date-picker__month-menu">
              {monthOptions.map((opt, i) => {
                const isActive = opt.month === monthIndex && opt.year === year;
                return (
                  <div
                    key={i}
                    className={`date-picker__month-item ${
                      isActive ? "date-picker__month-item--active" : ""
                    }`}
                    onClick={() => {
                      // If they change the left calendar, set viewDate to that month
                      // If they change the right calendar, set viewDate to the month BEFORE it
                      if (position === "left") {
                        setViewDate(new Date(opt.year, opt.month, 1));
                      } else {
                        setViewDate(new Date(opt.year, opt.month - 1, 1));
                      }
                      setOpenDropdown(null);
                    }}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="date-picker__weekdays">
          {daysOfWeek.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>

        <div className="date-picker__grid">
          {days.map((d, i) => {
            const selected = isSelected(d.date);
            const inRange = isInRange(d.date);
            const isStart =
              startDate && d.date.getTime() === startDate.getTime();
            const isEnd = endDate && d.date.getTime() === endDate.getTime();

            return (
              <button
                key={i}
                onClick={() => handleDateClick(d.date)}
                disabled={!d.isCurrentMonth}
                className={`
                  date-picker__day 
                  ${!d.isCurrentMonth ? "date-picker__day--dimmed" : ""}
                  ${selected ? "date-picker__day--selected" : ""}
                  ${inRange ? "date-picker__day--in-range" : ""}
                  ${isStart && endDate ? "date-picker__day--start" : ""}
                  ${isEnd ? "date-picker__day--end" : ""}
                `}
              >
                <span className="date-picker__day-number">{d.day}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <BaseDropdown
      className="date-picker-dropdown-override"
      align="left"
      trigger={
        <button className="latest-records__date-picker">
          <span>{formatDate(startDate)}</span>
          <ArrowRightIcon />
          <span>{formatDate(endDate)}</span>
          <CalendarIcon />
        </button>
      }
    >
      {({ closeDropdown }) => (
        <div className="date-picker__wrapper">
          <div className="date-picker__body">
            {renderCalendar(month1, year1, month1Days, "left")}
            {renderCalendar(month2, year2, month2Days, "right")}
          </div>

          {/* Footer */}
          <div className="date-picker__footer">
            <span className="date-picker__selected-text">
              {startDate
                ? `Selected: ${formatDate(startDate)} to ${formatDate(endDate)}`
                : "No dates selected"}
            </span>

            {/* NEW: Action Buttons Wrapper */}
            <div className="date-picker__actions">
              <button
                className="date-picker__reset-btn"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  if (onConfirm) onConfirm({ from: null, to: null });
                  closeDropdown();
                }}
              >
                Reset
              </button>

              <button
                className="date-picker__confirm-btn"
                onClick={() => {
                  if (onConfirm) onConfirm({ from: startDate, to: endDate });
                  closeDropdown();
                }}
                disabled={!startDate || !endDate}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </BaseDropdown>
  );
};
